/** @format */

import {useState, useEffect, ChangeEvent} from 'react';

import Alert from '@mui/material/Alert';
import {InputAdornment, Box, Button, LinearProgress, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import QrCode from 'react-qr-code';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';

import clsx from 'clsx';

import {People} from 'mixpanel-browser';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import Breadcrumbs from 'components/Breadcrumbs';
import LegacyTextField from 'components/inputs/LegacyTextField';
import PasswordField from 'components/inputs/PasswordField';
import PhoneNumberField from 'components/inputs/PhoneNumberField';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {useStyles} from './styles';
import {
  useCreateProfileMutation,
  useDisableTwoFaMutation,
  useEnableTwoFaMutation,
  useGetCurrentUserQuery,
  useUpdatePasswordMutation,
  useUpdateProfileMutation,
} from './graphql/__generated__';

const CODE_LENGTH = 6;

const breadcrumbs = [
  {
    href: '/home/settings',
    title: <Trans>Настройки</Trans>,
  },
  {
    href: '',
    title: <Trans>Настройки аккаунта</Trans>,
  },
];

type StateType = {
  name: string;
  currentPassword: string;
  password: string;
  passwordConfirmation: string;
};

const Profile = () => {
  const {mixpanel: untypedMixpanel} = useMixpanelContext();
  const mixpanel = untypedMixpanel as typeof untypedMixpanel & {people: People};

  const classes = useStyles();

  const {t} = useTranslation();

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const [state, setState] = useState<StateType>({
    name: '',
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  });

  const [passwordFormVisible, setPasswordFormVisible] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const {loading, error, data, refetch} = useGetCurrentUserQuery();
  const [createProfile, {error: createProfileError}] = useCreateProfileMutation();
  const [updateProfile, {error: updateProfileError}] = useUpdateProfileMutation();
  const [updatePassword, {error: updatePasswordError}] = useUpdatePasswordMutation();

  const [enable2Fa, {loading: enable2FaLoading}] = useEnableTwoFaMutation();
  const [disable2Fa, {loading: disable2FaLoading}] = useDisableTwoFaMutation();

  useEffect(() => {
    if (!data?.currentUser) return;

    const {profile} = data.currentUser;
    setState(prev => ({...prev, name: (profile && profile.name) || ''}));
  }, [data]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  if (loading || !data?.currentUser) {
    return <LinearProgress style={{flex: 1}} data-testid='loading' />;
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setState({...state, [name]: value});
  };

  const handleBlurName = async () => {
    mixpanel.track('click_edit_name');
    const profile = data.currentUser?.profile;

    if (profile) {
      await updateProfile({variables: {name: state.name}});
    } else {
      await createProfile({variables: {name: state.name}});
    }

    mixpanel.people.set({$name: state.name, name: state.name});
    mixpanel.register({name: state.name});

    await refetch();
  };

  const changePassword = async () => {
    mixpanel.track('click_edit_password');

    await updatePassword({
      variables: {
        currentPassword: state.currentPassword,
        newPassword: state.password,
      },
    });

    setPasswordFormVisible(false);
    setPasswordUpdated(true);
  };

  const handleBlurPassword = () => {
    if (!state.password || !state.passwordConfirmation) {
      setNewPasswordError(null);
      return;
    }
    if (state.password !== state.passwordConfirmation) {
      setNewPasswordError(
        t(
          'Не совпадает новый пароль и подтверждение пароля',
          'Не совпадает новый пароль и подтверждение пароля',
        ),
      );
      return;
    } else {
      setNewPasswordError(null);
    }
    if (!state.currentPassword) return;

    changePassword();
  };

  const saveProfileError = createProfileError || updateProfileError;

  const {phone, has2fa, secret} = data.currentUser;

  const renderQrCode = () => {
    // https://hexdocs.pm/nimble_totp/NimbleTOTP.html#otpauth_uri/3
    const label = `AdSensor:${phone}`;
    const query = `secret=${secret}&issuer=AdSensor`;
    const otpauthUri = `otpauth://totp/${label}?${query}`;

    // qr code should have "quit zone"
    // https://qrworld.wordpress.com/2011/08/09/the-quiet-zone/
    return (
      <Box display='flex' justifyContent='center' margin={'0 auto'} bgcolor='white'>
        <QrCode value={otpauthUri} />
      </Box>
    );
  };

  const renderCodeInput = () => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setCode(event.target.value);
    };

    const handleDisable = async () => {
      await disable2Fa();
      refetch();
    };

    const handleEnable = async () => {
      try {
        await enable2Fa({variables: {secret, code}});
        refetch();
      } catch (e) {
        if (e instanceof Error) {
          setCodeError(e.message === 'invalid_code' ? t('Неправильный код') : e.message);
        }
      }
    };

    return (
      <Box className={classes.twoFAContainer} mr={4}>
        <Box flexGrow={1} mr={4} className={classes.twoFAInnerContainer}>
          <label htmlFor='2fa' className={classes.label}>
            <Trans>Код аутентификации</Trans>
          </label>

          <LegacyTextField
            className={classes.twoFATextField}
            error={Boolean(codeError)}
            helperText={codeError}
            placeholder={'••••••••••••'}
            value={code}
            onChange={handleChange}
            slotProps={{
              htmlInput: {name: '2fa', maxLength: CODE_LENGTH},
              input: {
                startAdornment: (
                  <InputAdornment position='end'>
                    <VpnKeyOutlinedIcon
                      className={classes.inputIcon}
                      htmlColor='#8B8B8B'
                      fontSize='small'
                    />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box>
          {has2fa && (
            <PrimaryMediumButton
              className={clsx(classes.twoFAButton, codeError && classes.twoFAButtonWithError)}
              disabled={disable2FaLoading}
              onClick={handleDisable}
            >
              <Trans>Отключить</Trans>
            </PrimaryMediumButton>
          )}

          {!has2fa && (
            <PrimaryMediumButton
              className={clsx(classes.twoFAButton, codeError && classes.twoFAButtonWithError)}
              disabled={enable2FaLoading || !code || code.length !== CODE_LENGTH}
              onClick={handleEnable}
            >
              <Trans>Включить</Trans>
            </PrimaryMediumButton>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box display='flex' flexDirection={'column'} width={'100%'}>
        <Box>
          <Breadcrumbs items={breadcrumbs} />
        </Box>
        <Box mt={14}>
          <Box>
            <Typography className={classes.headerTitle}>
              <Trans>Профиль пользователя</Trans>
            </Typography>
          </Box>

          <Box mt={6}>
            <Box className={classes.rowNew}>
              <label htmlFor='name' className={classes.label}>
                <Trans>Имя пользователя</Trans>
              </label>

              <LegacyTextField
                className={classes.textField}
                errorText={saveProfileError?.message}
                helperText={saveProfileError == null ? '' : saveProfileError.message}
                myProps={{placeholderFocused: ''}}
                placeholder={t('Имя', 'Имя')}
                value={state.name}
                onBlur={handleBlurName}
                onChange={handleChange}
                slotProps={{
                  htmlInput: {
                    name: 'name',
                  },
                  input: {
                    startAdornment: (
                      <InputAdornment position='end'>
                        <PersonOutlineIcon
                          className={classes.inputIcon}
                          htmlColor='#8B8B8B'
                          fontSize='small'
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {data.currentUser.phone && (
              <Box className={classes.rowNew} mt={2}>
                <label htmlFor='name' className={classes.label}>
                  <Trans>Номер телефона</Trans>
                </label>

                <PhoneNumberField
                  className={classes.textField}
                  value={data.currentUser.phone}
                  onChangePhone={() => {}}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position='end'>
                          <SmartphoneOutlinedIcon
                            className={classes.inputIcon}
                            htmlColor='#8B8B8B'
                            fontSize='small'
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                  disabled
                />
              </Box>
            )}

            {!passwordFormVisible && (
              <Box className={classes.rowNew} mt={2}>
                <label htmlFor='name' className={classes.label}>
                  <Trans>Пароль</Trans>
                </label>

                <Button
                  className={classes.passwordButtonNew}
                  color='secondary'
                  variant='outlined'
                  onClick={() => setPasswordFormVisible(!passwordFormVisible)}
                >
                  <Box>
                    <HttpsOutlinedIcon htmlColor='#8B8B8B' fontSize='small' />
                    <Box component={'span'} className={classes.hiddenPasswordField}>
                      ••••••••••••
                    </Box>
                  </Box>

                  {!passwordUpdated && (
                    <img src={new URL('./images/edit.svg', import.meta.url).href} alt='Profile' />
                  )}
                  {passwordUpdated && (
                    <img src={new URL('./images/done.svg', import.meta.url).href} alt='Done' />
                  )}
                </Button>
                {passwordUpdated && (
                  <Box className={classes.successMessage}>
                    <Trans>Пароль успешно изменен</Trans>
                  </Box>
                )}
              </Box>
            )}
            {passwordFormVisible && (
              <>
                <Box className={classes.rowNew} mt={2}>
                  <PasswordField
                    className={classes.textField}
                    error={Boolean(updatePasswordError)}
                    helperText={updatePasswordError ? updatePasswordError.message : ''}
                    myProps={{
                      placeholderFocused: t('Текущий пароль', 'Текущий пароль'),
                    }}
                    placeholder={t('Текущий пароль', 'Текущий пароль')}
                    value={state.currentPassword}
                    onBlur={handleBlurPassword}
                    onChange={handleChange}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='end'>
                            <HttpsOutlinedIcon
                              className={classes.inputIcon}
                              htmlColor='#8B8B8B'
                              fontSize='small'
                            />
                          </InputAdornment>
                        ),
                      },
                      htmlInput: {
                        name: 'currentPassword',
                      },
                    }}
                  />
                </Box>
                <Box className={classes.rowNew} mt={2}>
                  <PasswordField
                    className={classes.textField}
                    error={Boolean(newPasswordError)}
                    myProps={{placeholderFocused: ''}}
                    placeholder={t('Новый пароль', 'Новый пароль')}
                    value={state.password}
                    onBlur={handleBlurPassword}
                    onChange={handleChange}
                    slotProps={{
                      htmlInput: {
                        name: 'password',
                      },
                      input: {
                        startAdornment: (
                          <InputAdornment position='end'>
                            <HttpsOutlinedIcon
                              className={classes.inputIcon}
                              htmlColor='#8B8B8B'
                              fontSize='small'
                            />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
                <Box className={classes.rowNew} mt={2}>
                  <PasswordField
                    className={classes.textField}
                    error={Boolean(newPasswordError)}
                    helperText={newPasswordError}
                    myProps={{placeholderFocused: ''}}
                    placeholder={t('Повторите пароль', 'Повторите пароль')}
                    value={state.passwordConfirmation}
                    onBlur={handleBlurPassword}
                    onChange={handleChange}
                    slotProps={{
                      htmlInput: {
                        name: 'passwordConfirmation',
                      },
                      input: {
                        startAdornment: (
                          <InputAdornment position='end'>
                            <HttpsOutlinedIcon
                              className={classes.inputIcon}
                              htmlColor='#8B8B8B'
                              fontSize='small'
                            />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>

          <Box mt={14}>
            <Typography className={classes.headerTitle}>
              <Trans>Безопасность</Trans>
            </Typography>
          </Box>

          <Box mt={6}>
            <Box className={classes.rowNew}>
              {has2fa ? (
                <p className={classes.securityTitle}>
                  <Trans>У вас включена двухфакторная аутентификация</Trans>
                </p>
              ) : (
                <>
                  <p className={classes.securityTitle}>
                    <Trans>У вас отключена двухфакторная аутентификация</Trans>
                  </p>
                  <p className={classes.securityDescription}>
                    <Trans>
                      Для подключения двухфакторной аутентификации отсканируйте QR код с помощью
                      вашего приложения для двухфакторной аутентификации и введите код
                      аутентификации в поле ввода.
                    </Trans>
                  </p>
                </>
              )}
            </Box>

            {!has2fa && (
              <Box className={classes.rowNew} mt={2}>
                {renderQrCode()}
              </Box>
            )}
            <Box className={clsx(classes.rowNew)} mt={2} mb={6}>
              {renderCodeInput()}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Profile;
